// frontend/src/pages/looks/NotificationsPage.js
// W3 — Notification inbox for any authenticated user. Lists notifications
// (likes, comments, follows, reposts), marks them read, and navigates to the
// related look / professional / dashboard.

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaBell, FaCheckDouble } from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= 768,
  );

  const getToken = () => localStorage.getItem("ogestyle_token");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Redirect unauthenticated visitors to login.
  useEffect(() => {
    const token = getToken();
    if (!token) {
      const path = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(path)}`);
    }
  }, [navigate]);

  const loadNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${DEFAULT_API}/api/notifications?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Unable to load notifications");
      setNotifications(data.notifications || []);
      setUnreadCount(Number(data.unreadCount) || 0);
    } catch (err) {
      setError(err.message || "Unable to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const markAsRead = async (notification) => {
    const token = getToken();
    if (!token) return;
    if (!notification.isRead) {
      try {
        await fetch(`${DEFAULT_API}/api/notifications/${notification._id}/read`, {
          method: "PATCH",
          headers: { Authorization: `Bearer ${token}` },
        });
        setNotifications((prev) =>
          prev.map((n) => (n._id === notification._id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        // Non-fatal.
      }
    }
  };

  const markAllAsRead = async () => {
    const token = getToken();
    if (!token) return;
    try {
      await fetch(`${DEFAULT_API}/api/notifications/mark-all-read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      setError("Unable to mark notifications read");
    }
  };

  const openNotification = async (notification) => {
    await markAsRead(notification);
    const actionUrl = notification.actionUrl;
    // Only navigate to internal app paths (never external URLs).
    if (actionUrl && actionUrl.startsWith("/") && !actionUrl.includes("://")) {
      navigate(actionUrl);
    }
  };

  const typeLabel = (type) => {
    const map = {
      follow: "New follower",
      look_like: "New like",
      look_comment: "New comment",
      look_repost: "New repost",
    };
    return map[type] || "Notification";
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <CustomerHeader />
      <main
        style={{
          maxWidth: "760px",
          margin: "0 auto",
          padding: isMobile
            ? "calc(var(--demo-banner-height, 0px) + 76px) 16px 48px"
            : "calc(var(--demo-banner-height, 0px) + 90px) 24px 64px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "10px", marginBottom: "18px" }}>
          <div>
            <Link to="/" style={{ color: "#2563eb", fontSize: "13px", fontWeight: 600, textDecoration: "none" }}>
              ← Home
            </Link>
            <h1 style={{ margin: "8px 0 2px", fontSize: isMobile ? "20px" : "26px", fontWeight: 700, color: "#0f172a" }}>
              <FaBell style={{ verticalAlign: "middle", marginRight: "8px", color: "#1d4f80" }} />
              Notifications
            </h1>
            <p style={{ margin: 0, color: "#6b7280", fontSize: "13px" }}>
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                border: "1px solid #d1d5db",
                borderRadius: "999px",
                background: "#fff",
                color: "#374151",
                padding: "9px 16px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <FaCheckDouble size={12} /> Mark all read
            </button>
          )}
        </div>

        {error && (
          <div style={{ marginBottom: "12px", padding: "10px 12px", border: "1px solid #fecaca", borderRadius: "8px", background: "#fef2f2", color: "#991b1b", fontSize: "13px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#9ca3af" }}>Loading…</div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280", border: "1px dashed #d1d5db", borderRadius: "12px" }}>
            <FaBell size={26} style={{ color: "#9ca3af", marginBottom: "8px" }} />
            <div>No notifications yet.</div>
            <div style={{ fontSize: "13px", marginTop: "4px" }}>
              When someone likes, comments, follows or reposts your looks, you'll see it here.
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {notifications.map((n) => {
              const isRead = !!n.isRead;
              return (
                <button
                  key={n._id}
                  onClick={() => openNotification(n)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    textAlign: "left",
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    background: isRead ? "#fff" : "#f1f6fb",
                    padding: "14px 16px",
                    cursor: "pointer",
                  }}
                >
                  <span
                    style={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "50%",
                      background: "#1d4f80",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      fontSize: "13px",
                    }}
                  >
                    {n.type === "look_like" ? "♥" : n.type === "look_comment" ? "💬" : n.type === "look_repost" ? "↻" : "＋"}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#0f172a" }}>
                      {n.title || typeLabel(n.type)}
                      {!isRead && (
                        <span style={{ display: "inline-block", marginLeft: "8px", width: "8px", height: "8px", borderRadius: "50%", background: "#f59e0b", verticalAlign: "middle" }} />
                      )}
                    </div>
                    {n.message && (
                      <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "2px" }}>{n.message}</div>
                    )}
                    <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "4px" }}>
                      {timeAgo(n.createdAt)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </main>
      <CustomerFooter />
    </div>
  );
}

export default NotificationsPage;
